import {createTheme, ThemeOptions} from '@material-ui/core/styles';
import * as createPalette from '@material-ui/core/styles/createPalette';
import * as createTypography from '@material-ui/core/styles/createTypography';

const defaultTheme = createTheme();
const {breakpoints} = defaultTheme;

const headingColor = '#707070';
const paragraphColor = '#7a7a7a';
const mediumGrey = '#CCCCCC';
const lightGrey = '#f0f0f0';
const white = '#fff';
const grey300 = '#e0e0e0';
const grey400 = '#dbd9e8';
const grey600 = '#646079';
const green = paragraphColor;
const pink = '#cb269f';
const yellow = '#FFB95B';
const red = '#cc0000';

type MUIRichTextEditorOptions = {
  container: any;
};

declare module '@material-ui/core/styles/overrides' {
  export interface ComponentNameToClassKey {
    MUIRichTextEditor: MUIRichTextEditorOptions;
  }
}

declare module '@material-ui/core/styles/shape' {
  interface Shape {
    borderRadius: number;
    borderRadiusLg: number;
    borderRadiusXl: number;
  }
}

declare module '@material-ui/core/styles/createTypography' {
  type LegitTypographyVariant = createTypography.Variant | 'bodySmall' | 'a';

  interface TypographyOptions {
    bodySmall?: createTypography.TypographyStyleOptions;
    a?: createTypography.TypographyStyleOptions;
  }

  export interface Typography
    extends Record<LegitTypographyVariant, createTypography.TypographyStyle>,
      createTypography.FontStyle,
      createTypography.TypographyUtils {}
}

type InteractionColor = {
  main: string;
};

type InteractionColors = {
  test: InteractionColor;
  event: InteractionColor;
  treatment: InteractionColor;
  document: InteractionColor;
};

type EventTypeColor = {
  light: string;
  main: string;
};

type EventTypeColors = {
  NEWP: EventTypeColor;
  TREA: EventTypeColor;
  FOLL: EventTypeColor;
  OTHE: EventTypeColor;
};

declare module '@material-ui/core/styles/createPalette' {
  interface PaletteOptions {
    pink?: createPalette.PaletteColorOptions;
    interaction?: InteractionColors;
    eventType?: EventTypeColors;
  }

  interface Palette {
    pink: createPalette.PaletteColor;
    eventType: {
      NEWP: createPalette.PaletteColor;
      TREA: createPalette.PaletteColor;
      FOLL: createPalette.PaletteColor;
      OTHE: createPalette.PaletteColor;
    };
    interaction: InteractionColors;
  }
}

type Layout = {
  headerHeight: {
    mobile: string;
    desktop: string;
  };
  containerPaddingHorizontal: number | string;
};

type Border = {
  light: string;
  default: string;
};

declare module '@material-ui/core/styles/createTheme' {
  interface ThemeOptions {
    layout: Layout;
    border: Border;
  }

  interface Theme {
    layout: Layout;
    border: Border;
  }
}

const options: ThemeOptions = {
  layout: {
    headerHeight: {
      mobile: '50px',
      desktop: '85px'
    },
    containerPaddingHorizontal: defaultTheme.spacing(2)
  },
  border: {
    light: '1px solid #F0F0F0',
    default: '1px solid #E5E5E7'
  },
  shape: {
    borderRadius: 0,
    borderRadiusLg: 0,
    borderRadiusXl: 0
  },
  palette: {
    primary: {
      light: lightGrey,
      main: headingColor,
      contrastText: white
    },
    secondary: {
      light: lightGrey,
      main: paragraphColor,
      contrastText: white
    },
    pink: {
      main: pink
    },
    error: {
      main: red
    },
    text: {
      primary: paragraphColor,
      secondary: headingColor
    },
    grey: {
      300: grey300,
      400: grey400,
      600: grey600
    },
    interaction: {
      test: {main: lightGrey},
      event: {main: paragraphColor},
      treatment: {main: pink},
      document: {main: grey300}
    },
    eventType: {
      NEWP: {
        light: '#FFE2E6',
        main: pink
      },
      TREA: {
        light: '#bfd9f1',
        main: lightGrey
      },
      FOLL: {
        light: '#c7ffee',
        main: green
      },
      OTHE: {
        light: '#FFF4E5',
        main: yellow
      }
    }
  },
  typography: {
    fontFamily: [
      'Montserrat',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"'
    ].join(','),
    bodySmall: {
      fontSize: '14px',
      [breakpoints.down('sm')]: {
        fontSize: '12px'
      }
    },
    body1: {
      fontSize: '16px',
      [breakpoints.down('sm')]: {
        fontSize: '14px'
      }
    },
    body2: {
      fontSize: '14px',
      [breakpoints.down('sm')]: {
        fontSize: '12px'
      }
    },
    h1: {
      fontSize: '32px',
      [breakpoints.down('sm')]: {
        fontSize: '28px'
      },
      fontWeight: 'bold',
      color: headingColor
      // marginBottom: '.6rem'
    },
    h2: {
      fontSize: '27px',
      [breakpoints.down('sm')]: {
        fontSize: '23px'
      },
      fontWeight: 'bold',
      color: headingColor
    },
    h3: {
      fontSize: '23px',
      [breakpoints.down('sm')]: {
        fontSize: '20px'
      },
      fontWeight: 'bold',
      color: headingColor
    },
    h4: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: headingColor
    },
    button: {
      textTransform: 'none'
    },
    a: {
      textDecoration: 'underline',
      color: headingColor
    }
  },
  overrides: {
    MUIRichTextEditor: {
      root: {},
      container: {
        border: '1px solid #c4c4c4',
        borderRadius: 13
      },
      toolbar: {
        borderBottom: '1px solid #c4c4c4'
      },
      editor: {
        padding: '20px',
        paddingTop: 0,
        height: '323px',
        maxHeight: '323px',
        overflow: 'auto'
      },
      placeHolder: {
        paddingLeft: 20
      },
      anchorLink: {
        color: green,
        textDecoration: 'underline'
      }
    },
    MuiTypography: {
      gutterBottom: {
        marginBottom: 0.5
      }
    },
    MuiAvatar: {
      colorDefault: {backgroundColor: mediumGrey, color: white}
    },
    MuiCssBaseline: {
      '@global': {
        a: {
          color: green,
          textDecoration: 'none'
        }
      }
    },
    MuiListItemText: {
      primary: {
        fontWeight: 'bold'
      },
      secondary: {
        fontSize: '16px'
      }
    },
    MuiListItemIcon: {
      root: {
        minWidth: 'inherit',
        marginRight: '16px'
      }
    },
    MuiTooltip: {
      tooltip: {
        fontSize: '1em',
        color: headingColor,
        backgroundColor: lightGrey,
        padding: '10px',
        fontWeight: 'normal'
      },
      arrow: {
        color: lightGrey,
        fontSize: '.66em'
      }
    },
    MuiDrawer: {
      paperAnchorDockedLeft: {
        borderRight: 0
      }
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: white,
        color: headingColor
      }
    },
    MuiStepConnector: {
      alternativeLabel: {
        top: 23
      }
    }
  }
};

export default createTheme(options);
